import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Grid, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AppLoader from "../../../components/apploader";
import GlobalButton from "../../../components/button";
import AuthContainer from "../../../components/container/AuthContainer";
import InputField from "../../../components/InputField";
import Label from "../../../components/label";
import { useSnackbar } from "../../../lib/context/snackbarContext";
import { fetchUserAddress } from "../../../lib/redux/slices/addressSlice";
import { setUserData } from "../../../lib/redux/slices/userSlice";
import { loginUser } from "../../../services/authenticationFn";
import { encryptPassword } from "../../../utils";
import { loginValidation } from "../../../utils/validation";
import { t } from "i18next";
import { Trans } from "react-i18next";

const forgetLinkStyle = {
  fontFamily: "Roboto",
  fontSize: "1rem",
  textDecoration: "none",
  textTransform: "capitalize",
  color: "black",
};

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [inputValues, setInputValues] = useState({
    email: "",
    password: "",
  });
  const [inputError, setInputError] = useState({
    email: false,
    isEmailValid: false,
    password: false,
  });

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;
    setInputError((err) => ({
      ...err,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
    }));
    setInputValues((val) => ({ ...val, [name]: value }));
  };

  const handleLogin = async () => {
    try {
      const validate = loginValidation(inputValues, setInputError);
      if (validate) return;

      const payload = {
        email: inputValues.email,
        password: encryptPassword(inputValues.password),
      };

      setIsLoading(true);
      const response = await loginUser(payload);
      if (response.data.status === "success") {
        const userData = response?.data?.data.user;
        const token = response?.data?.data.token;

        const userWithToken = { ...userData, token };
        localStorage.setItem("user_data", JSON.stringify(userWithToken));
        dispatch(fetchUserAddress(userData._id));
        dispatch(setUserData(userWithToken));
        showSnackbar(response.data.message, "success");
        navigate("/");
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AppLoader isVisible={isLoading} />
      <Stack
        position={"absolute"}
        top={20}
        left={30}
        direction={"row"}
        alignItems={"center"}
        gap={1}
      >
        <Typography variant="body1" color={"#8692A6"}>
          {t("createAccount")} ?
        </Typography>
        <Typography
          variant="body1"
          fontWeight={600}
          color={"primary.main"}
          sx={{ cursor: "pointer", textTransform: "capitalize" }}
          onClick={() => navigate("/register")}
        >
          {t("signUp")}
        </Typography>
      </Stack>
      <Stack textAlign={"center"} mt={6}>
        <Typography variant="heading_pr" textTransform={"capitalize"}>
          {t("logIn")}
        </Typography>
        <Trans i18nKey="welcomeLogin">
          Welcome to <b style={{ color: "black" }}>Market</b> CRM, Please give
          us user id and password
        </Trans>
      </Stack>

      <Grid container rowSpacing={2} mt={3}>
        <Grid item xs={12}>
          <Label text={t("emailAddress")} required htmlFor="user-email" />
          <InputField
            id={"user-email"}
            required
            inputError={inputError.email || inputError.isEmailValid}
            helperText={
              inputError.email
                ? t("emailAddRequired")
                : inputError.isEmailValid
                ? t("invalidEmailFormat")
                : null
            }
            placeholder={t("enterEmailAdd")}
            type={"email"}
            name={"email"}
            value={inputValues.email}
            onChange={handleChangeInputs}
          />
        </Grid>
        <Grid item xs={12}>
          <Label text={t("password")} required htmlFor="user_log_pass" />
          <InputField
            id={"user_log_pass"}
            placeholder={t("enterNewPass")}
            type={showPassword ? "password" : "text"}
            InputEndIcon={
              showPassword ? (
                <VisibilityOffIcon
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => setShowPassword(false)}
                />
              ) : (
                <VisibilityIcon
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => setShowPassword(true)}
                />
              )
            }
            inputError={inputError.password}
            helperText={inputError.password && t("passwordRequired")}
            name={"password"}
            value={inputValues.password}
            onChange={handleChangeInputs}
          />
        </Grid>
      </Grid>
      <Stack alignItems={"flex-end"} my={1}>
        <Link style={forgetLinkStyle} to={"/forget-password"}>
          {t("forgotPassword")}
        </Link>
      </Stack>

      <GlobalButton
        btnText={"logIn"}
        handleClickBtn={handleLogin}
        styleProps={{
          width: "100%",
          height: 50,
          mt: 4,
          fontSize: "1.125rem",
        }}
      />

      {/* <SocialLogins /> */}
    </AuthContainer>
  );
};

export default LoginPage;
